import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTraceSignature } from '../components/Shared';
import { Loading, ouvrirDocument, niveauMessage, styleMessage } from '../components/Shared';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUTS = [
  { val: 'recue',    label: 'Reçue',    color: '#92400e', bg: '#fef3c7' },
  { val: 'en_cours', label: 'En cours', color: '#1e40af', bg: '#dbeafe' },
  { val: 'resolue',  label: 'Résolue',  color: '#065f46', bg: '#d1fae5' },
  { val: 'cloturee', label: 'Clôturée', color: '#64748b', bg: '#f1f5f9' },
];

const MODES = ['Email', 'Téléphone', 'Courrier', 'En personne', 'Autre'];

// ── Canvas signature ──────────────────────────────────────────────────────────
function SignatureCanvas({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const trace = useTraceSignature(canvasRef, { largeurTrait: 2.5 });

  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const save = () => {
    const canvas = canvasRef.current;
    const data = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
    if (!data.some((v, i) => i % 4 === 3 && v > 0)) { alert('Veuillez signer avant de valider.'); return; }
    onSave(canvas.toDataURL('image/png'));
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
    canvas.getContext('2d').scale(dpr, dpr);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', textAlign: 'center' }}>Tracez votre signature ci-dessous</div>
      <canvas ref={canvasRef}
        style={{ width: '100%', height: 110, border: '2px solid #1a3668', borderRadius: '6px', background: '#f8faff', touchAction: 'none', cursor: 'crosshair' }}
        {...trace} />
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={clear} style={{ flex: 1, padding: '0.4rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>Effacer</button>
        <button onClick={onCancel} style={{ flex: 1, padding: '0.4rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>Annuler</button>
        <button onClick={save} className="btn-primary" style={{ flex: 2, fontSize: '0.82rem' }}>✓ Valider</button>
      </div>
    </div>
  );
}

// ── Bloc signature affiché ───────────────────────────────────────────────────
function SigBlock({ label, value, dateValue, onSign, onDelete, signerLabel }) {
  const [showPad, setShowPad] = useState(false);
  const handleSave = async (b64) => { setShowPad(false); await onSign(b64); };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem', minWidth: 200 }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '0.4rem' }}>{label}</div>
      {value ? (
        <div>
          <img src={value} alt="signature" style={{ maxHeight: 55, maxWidth: '100%', objectFit: 'contain', display: 'block', marginBottom: '0.3rem' }} />
          {dateValue && <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Signé le {new Date(dateValue).toLocaleDateString('fr-FR')}</div>}
          {onDelete && <button onClick={onDelete} style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Supprimer</button>}
        </div>
      ) : showPad ? (
        <SignatureCanvas onSave={handleSave} onCancel={() => setShowPad(false)} />
      ) : (
        <button onClick={() => setShowPad(true)}
          style={{ width: '100%', padding: '0.6rem', border: '1.5px dashed var(--border)', borderRadius: '6px', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.82rem' }}>
          ✍ {signerLabel || 'Signer'}
        </button>
      )}
    </div>
  );
}

// ── Helpers UI ───────────────────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }}>{hint}</div>}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>{children}</div>;
}

function SectionCard({ title, bg = 'var(--bg-card)', children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', marginBottom: '1.25rem' }}>
      <div style={{ background: '#0f172a', color: '#fff', padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '.04em' }}>{title}</div>
      <div style={{ padding: '1rem', background: bg }}>{children}</div>
    </div>
  );
}

function OuiNon({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      {['OUI', 'NON'].map(v => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          style={{ padding: '0.3rem 1rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${value === v ? (v === 'OUI' ? '#16a34a' : '#dc2626') : 'var(--border)'}`,
            background: value === v ? (v === 'OUI' ? '#d1fae5' : '#fee2e2') : 'transparent',
            color: value === v ? (v === 'OUI' ? '#065f46' : '#b91c1c') : 'var(--text-secondary)' }}>
          {v}
        </button>
      ))}
    </div>
  );
}

const inputStyle = { width: '100%', padding: '0.45rem 0.7rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' };
const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 };

export default function ReclamationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [objets, setObjets] = useState([]);
  const [emailModal, setEmailModal] = useState(null); // 'accuse' | 'cloture'
  const [emailDest, setEmailDest] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = useCallback(() => {
    apiFetch(`/reclamations/${id}`)
      .then(d => { setRec(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
    apiFetch('/clients?limit=500').then(d => setClients(d?.data || [])).catch(() => {});
    apiFetch('/sessions?limit=500').then(d => setSessions(d?.data || d || [])).catch(() => {});
    apiFetch('/parametres').then(p => {
      try { setObjets(JSON.parse(p.reclamation_objets || '[]')); } catch { setObjets([]); }
    }).catch(() => {});
  }, [load]);

  function set(key, val) { setRec(r => ({ ...r, [key]: val })); }

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      const updated = await apiFetch(`/reclamations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(rec),
      });
      setRec(updated);
      setMsg('✓ Réclamation enregistrée');
    } catch (e) { setMsg('Erreur : ' + e.message); }
    setSaving(false);
  }

  async function handleSign(endpoint, body, reload = true) {
    try {
      await apiFetch(`/reclamations/${id}/${endpoint}`, { method: 'POST', body: JSON.stringify(body) });
      if (reload) load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  function handlePdf() {
    ouvrirDocument({ titre: `Réclamation ${rec?.reference || id}`, url: `/reclamations/${id}/pdf` });
  }

  async function handleEmail(type) {
    setSendingEmail(true);
    try {
      await apiFetch(`/reclamations/${id}/envoyer-${type}`, {
        method: 'POST',
        body: JSON.stringify({ email_destinataire: emailDest }),
      });
      setMsg(type === 'accuse' ? '✓ Accusé de réception envoyé' : '✓ Email de clôture envoyé');
      setEmailModal(null);
      load();
    } catch (e) { setMsg('Erreur : ' + e.message); }
    setSendingEmail(false);
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer la réclamation ${rec?.reference} ? Cette action est irréversible.`)) return;
    await apiFetch(`/reclamations/${id}`, { method: 'DELETE' });
    navigate('/reclamations');
  }

  if (loading || !rec) return <Loading />;

  const statutInfo = STATUTS.find(s => s.val === rec.statut) || STATUTS[0];

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div>
          <button onClick={() => navigate('/reclamations')}
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem', padding: 0, marginBottom: '0.3rem' }}>
            ← Réclamations
          </button>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            {rec.reference}
          </h1>
          <div style={{ marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ padding: '0.2rem 0.7rem', borderRadius: '10px', fontSize: '0.78rem', fontWeight: 700,
              background: statutInfo.bg, color: statutInfo.color }}>
              {statutInfo.label}
            </span>
            {rec.jours_ecoules != null && rec.statut !== 'cloturee' && (
              <span style={{ fontSize: '0.78rem', color: Number(rec.jours_ecoules) > 10 ? '#dc2626' : 'var(--text-tertiary)' }}>
                {rec.jours_ecoules} jour{rec.jours_ecoules > 1 ? 's' : ''} depuis la réception
                {Number(rec.jours_ecoules) > 10 ? ' ⚠ En retard' : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={handlePdf}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem' }}>
            📄 PDF
          </button>
          <button onClick={() => { setEmailDest(rec.contact_email || ''); setEmailModal('accuse'); }}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem' }}>
            📧 Accusé réception
          </button>
          <button onClick={() => { setEmailDest(rec.contact_email || ''); setEmailModal('cloture'); }}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid #16a34a', background: '#d1fae5', color: '#065f46', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
            ✓ Clôturer &amp; notifier
          </button>
          <button onClick={handleDelete}
            style={{ padding: '0.4rem 0.9rem', borderRadius: '7px', border: '1px solid #fca5a5', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem' }}>
            Supprimer
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ marginBottom: '1rem', padding: '0.5rem 0.9rem', borderRadius: '7px', fontSize: '0.85rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}

      {/* Statut + saisi par */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Field label="Statut">
            <select value={rec.statut || 'recue'} onChange={e => set('statut', e.target.value)} style={inputStyle}>
              {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Field label="Date de réception">
            <input type="date" value={rec.date_reclamation?.slice(0, 10) || ''} onChange={e => set('date_reclamation', e.target.value)} style={inputStyle} />
          </Field>
        </div>
        <div style={{ flex: 2, minWidth: 200 }}>
          <Field label="Saisi par"  hint={rec.saisi_par_nom || ''}>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', padding: '0.45rem 0' }}>{rec.saisi_par_nom || user?.nom || '—'}</div>
          </Field>
        </div>
      </div>

      {/* Liens optionnels */}
      <Row>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Field label="Client (optionnel)">
            <select value={rec.client_id || ''} onChange={e => set('client_id', e.target.value || null)} style={inputStyle}>
              <option value="">— Aucun client —</option>
              {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Field label="Session liée (optionnel)">
            <select value={rec.session_id || ''} onChange={e => set('session_id', e.target.value || null)} style={inputStyle}>
              <option value="">— Aucune session —</option>
              {sessions.map(s => <option key={s.session_id} value={s.session_id}>{s.session_id} — {s.intitule_formation}</option>)}
            </select>
          </Field>
        </div>
      </Row>

      {/* SECTION 1 */}
      <SectionCard title="SECTION 1 — IDENTIFICATION DE LA RÉCLAMATION">
        <Row>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Interlocuteur (émetteur de la réclamation)">
              <input value={rec.interlocuteur || ''} onChange={e => set('interlocuteur', e.target.value)} style={inputStyle} placeholder="Nom, prénom, fonction…" />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Personne concernée">
              <input value={rec.personne_concernee || ''} onChange={e => set('personne_concernee', e.target.value)} style={inputStyle} placeholder="Stagiaire, formateur…" />
            </Field>
          </div>
        </Row>
        <Row>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Mode de réponse souhaité">
              <select value={rec.mode_reponse || ''} onChange={e => set('mode_reponse', e.target.value)} style={inputStyle}>
                <option value="">— Choisir —</option>
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ flex: 2, minWidth: 260 }}>
            <Field label="Objet de la réclamation">
              {objets.length > 0 ? (
                <select value={rec.objet || ''} onChange={e => set('objet', e.target.value)} style={inputStyle}>
                  <option value="">— Sélectionner —</option>
                  {objets.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={rec.objet || ''} onChange={e => set('objet', e.target.value)} style={inputStyle} placeholder="Objet de la réclamation" />
              )}
            </Field>
          </div>
        </Row>
        <Field label="Description détaillée">
          <textarea value={rec.description || ''} onChange={e => set('description', e.target.value)} style={textareaStyle} placeholder="Description complète de la réclamation…" />
        </Field>
        <Field label="Solution immédiate apportée">
          <textarea value={rec.solution_immediate || ''} onChange={e => set('solution_immediate', e.target.value)} style={{ ...textareaStyle, minHeight: 60 }} placeholder="Action immédiate mise en place…" />
        </Field>

        {/* Signatures section 1 */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <SigBlock
              label="Signature du plaignant"
              value={rec.signature_plaignant}
              dateValue={rec.date_signature_plaignant}
              signerLabel="Signer (plaignant)"
              onSign={b64 => handleSign('signature-plaignant', { signature: b64 })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <SigBlock
              label="Signature du référent (accusé de réception)"
              value={rec.signature_referent}
              dateValue={rec.date_signature_referent}
              signerLabel="Signer (référent)"
              onSign={b64 => handleSign('signature-referent', { signature: b64, referent_nom: user?.nom || '' })}
            />
            {rec.referent_nom && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>Par : {rec.referent_nom}</div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* SECTION 2 */}
      <SectionCard title="SECTION 2 — TRAITEMENT DE LA RÉCLAMATION (CEJ CONCEPT)">
        <Row>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Action curative satisfaisante ?">
              <OuiNon value={rec.curative_satisfaisante || ''} onChange={v => set('curative_satisfaisante', v)} />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Réclamation levée ?">
              <OuiNon value={rec.curative_levee || ''} onChange={v => set('curative_levee', v)} />
            </Field>
          </div>
        </Row>
        <Field label="Commentaire">
          <textarea value={rec.curative_commentaire || ''} onChange={e => set('curative_commentaire', e.target.value)} style={{ ...textareaStyle, minHeight: 60 }} placeholder="Commentaire sur le traitement curatif…" />
        </Field>
        <Row>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="Date de clôture curative">
              <input type="date" value={rec.curative_date?.slice(0, 10) || ''} onChange={e => set('curative_date', e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <div style={{ flex: 2, minWidth: 240 }}>
            <SigBlock
              label="Visa référent"
              value={rec.curative_visa}
              signerLabel="Apposer le visa"
              onSign={b64 => handleSign('signature-referent', { signature: b64, referent_nom: user?.nom || '' })}
            />
          </div>
        </Row>
      </SectionCard>

      {/* SECTION 3 */}
      <SectionCard title="SECTION 3 — ACTION CORRECTIVE &amp; VÉRIFICATION D'EFFICACITÉ">
        <Field label="Action corrective nécessaire ?">
          <OuiNon value={rec.corrective_necessaire || ''} onChange={v => set('corrective_necessaire', v)} />
        </Field>
        <Field label="Description de l'action corrective">
          <textarea value={rec.corrective_commentaire || ''} onChange={e => set('corrective_commentaire', e.target.value)} style={textareaStyle} placeholder="Actions correctives mises en place…" />
        </Field>
        <Row>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="Date de vérification d'efficacité">
              <input type="date" value={rec.efficacite_date?.slice(0, 10) || ''} onChange={e => set('efficacite_date', e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <Field label="Efficacité vérifiée ?">
              <OuiNon value={rec.efficacite_ok === true ? 'OUI' : rec.efficacite_ok === false ? 'NON' : ''} onChange={v => set('efficacite_ok', v === 'OUI' ? true : v === 'NON' ? false : null)} />
            </Field>
          </div>
          <div style={{ flex: 2, minWidth: 240 }}>
            <SigBlock
              label="Visa vérificateur d'efficacité"
              value={rec.efficacite_visa}
              signerLabel="Apposer le visa"
              onSign={b64 => handleSign('signature-efficacite', { signature: b64 })}
            />
          </div>
        </Row>
      </SectionCard>

      {/* Bouton enregistrer */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button onClick={() => navigate('/reclamations')}
          style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          Retour
        </button>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : '✓ Enregistrer'}
        </button>
      </div>

      {/* Modal email */}
      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 1rem', color: 'var(--text-primary)' }}>
              {emailModal === 'accuse' ? 'Envoyer un accusé de réception' : 'Notifier la clôture'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              {emailModal === 'accuse'
                ? `Un email confirmera la prise en charge de la réclamation ${rec.reference}.`
                : `La réclamation ${rec.reference} sera marquée comme clôturée.`}
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Email destinataire</label>
              <input type="email" value={emailDest} onChange={e => setEmailDest(e.target.value)} style={inputStyle} placeholder="client@email.fr" />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setEmailModal(null)} style={{ padding: '0.4rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Annuler</button>
              <button className="btn-primary" onClick={() => handleEmail(emailModal)} disabled={sendingEmail || !emailDest}>
                {sendingEmail ? '⏳ Envoi…' : '📧 Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
